import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Users, Clock, Package, Plus, Trash2, Pencil, Building2, ImagePlus } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";
import { imageToDataUrl } from "@/lib/files";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

const EMPTY = { name: "", address: "", instructors: [], schedule: [], stock: [], image: "" };

export default function Unidades() {
  const { user } = useAppUser() || {};
  const location = useLocation();
  const [units, setUnits] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const isAdmin = user && (user.roles || [user.role]).includes("admin");

  useEffect(() => {
    const novo = !!new URLSearchParams(location.search).get("novo");
    if (!novo) setEditingId(null);
    if (novo) { setEditingId(null); setForm(EMPTY); }
    setFormOpen(novo);
  }, [location.search]);

  const refresh = async () => {
    const res = await authApi().get("/units");
    setUnits(res.data.units);
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    if ((user.roles || [user.role]).includes("admin")) {
      authApi()
        .get("/users")
        .then((res) => setInstructors(res.data.users.filter((u) => (u.roles || [u.role]).includes("instrutor"))))
        .catch(() => {});
    }
  }, [user]);

  const toggleInstructor = (email) =>
    setForm((f) => ({
      ...f,
      instructors: f.instructors.includes(email) ? f.instructors.filter((e) => e !== email) : [...f.instructors, email],
    }));

  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...f[key], row] }));
  const setRow = (key, i, k, v) => setForm((f) => ({ ...f, [key]: f[key].map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const removeRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await authApi().patch(`/units/${editingId}`, form);
        toast.success("Unidade atualizada.");
        setEditingId(null);
        setFormOpen(false);
      } else {
        await authApi().post("/units", form);
        toast.success("Unidade criada.");
        setFormOpen(false);
      }
      setForm(EMPTY);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setFormOpen(true);
    setForm({
      name: u.name,
      address: u.address,
      instructors: (u.instructors || []).map((i) => i.email),
      schedule: u.schedule || [],
      stock: u.stock || [],
      image: u.image || "",
    });
    document.querySelector('[data-testid="unit-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await imageToDataUrl(file, 1200, 0.8);
      setForm((f) => ({ ...f, image: dataUrl }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir a unidade "${u.name}"?`)) return;
    try {
      await authApi().delete(`/units/${u.id}`);
      toast.success("Unidade removida.");
      if (editingId === u.id) { setEditingId(null); setForm(EMPTY); }
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AppShell active="unidades">
      <section data-testid="units-page">
        <h1 className="font-display text-2xl font-bold" data-testid="units-title">Unidades</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6" data-testid="units-user-info">
          UNIDADES DO PROJETO {units ? `(${units.length})` : ""}
        </p>

        {isAdmin && formOpen && (
          <form onSubmit={submit} className="bg-surface border border-white/10 rounded-2xl p-6 space-y-4" data-testid="unit-form">
            <p className="font-mono text-xs tracking-[0.3em] text-gold" data-testid="unit-form-label">
              {editingId ? "EDITAR UNIDADE" : "CADASTRAR UNIDADE"}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome da unidade (ex: Sede Além Ponte)" className={inputCls} data-testid="unit-name-input" />
              <input required minLength={2} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Endereço completo" className={inputCls} data-testid="unit-address-input" />
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">IMAGEM DA UNIDADE</p>
              <div className="flex items-center gap-4 flex-wrap">
                {form.image && (
                  <img src={form.image} alt="Prévia da unidade" className="w-28 h-20 object-cover rounded-lg border border-white/10" data-testid="unit-image-preview" />
                )}
                <label className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-4 py-2.5 rounded-full hover:border-gold/50 hover:text-gold transition-colors cursor-pointer" data-testid="unit-image-button">
                  <ImagePlus size={14} /> {form.image ? "TROCAR IMAGEM" : "ADICIONAR IMAGEM"}
                  <input type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="unit-image-input" />
                </label>
                {form.image && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, image: "" }))} data-testid="unit-image-remove" className="font-mono text-[11px] tracking-widest text-zinc-500 hover:text-crimson transition-colors">
                    REMOVER
                  </button>
                )}
              </div>
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">INSTRUTORES RESPONSÁVEIS</p>
              <div className="flex flex-wrap gap-2" data-testid="unit-instructors-group">
                {instructors.length === 0 && <p className="text-xs text-zinc-600">Nenhum usuário com perfil Instrutor cadastrado.</p>}
                {instructors.map((u) => (
                  <button
                    key={u.email}
                    type="button"
                    onClick={() => toggleInstructor(u.email)}
                    data-testid={`unit-instructor-${u.email.replace(/[^a-z0-9]/g, "-")}`}
                    className={`font-mono text-[11px] tracking-wider px-3 py-2 rounded-full border transition-colors ${form.instructors.includes(u.email) ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40"}`}
                  >
                    {u.name.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[11px] tracking-widest text-zinc-500">TREINOS DA SEMANA</p>
                <button type="button" onClick={() => addRow("schedule", { day: "Segunda", start: "19:00", end: "20:30", label: "" })} data-testid="unit-schedule-add" className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wider text-gold border border-gold/40 px-3 py-1.5 rounded-full hover:bg-gold/10 transition-colors">
                  <Plus size={12} /> ADICIONAR
                </button>
              </div>
              <div className="space-y-2" data-testid="unit-schedule-rows">
                {form.schedule.map((s, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-center">
                    <select value={s.day} onChange={(e) => setRow("schedule", i, "day", e.target.value)} className={inputCls} data-testid={`unit-schedule-day-${i}`}>
                      {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input value={s.label || ""} onChange={(e) => setRow("schedule", i, "label", e.target.value)} placeholder="Turma (ex: Kids, Adultos)" className={inputCls} data-testid={`unit-schedule-label-${i}`} />
                    <input type="time" value={s.start} onChange={(e) => setRow("schedule", i, "start", e.target.value)} className={`${inputCls} [color-scheme:dark] sm:w-28`} data-testid={`unit-schedule-start-${i}`} />
                    <input type="time" value={s.end} onChange={(e) => setRow("schedule", i, "end", e.target.value)} className={`${inputCls} [color-scheme:dark] sm:w-28`} data-testid={`unit-schedule-end-${i}`} />
                    <button type="button" onClick={() => removeRow("schedule", i)} data-testid={`unit-schedule-remove-${i}`} className="w-9 h-9 rounded-lg border border-white/15 flex items-center justify-center text-zinc-500 hover:text-crimson hover:border-crimson/60 transition-colors" aria-label="Remover horário">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[11px] tracking-widest text-zinc-500">ESTOQUE DA UNIDADE</p>
                <button type="button" onClick={() => addRow("stock", { name: "", quantity: 0 })} data-testid="unit-stock-add" className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wider text-gold border border-gold/40 px-3 py-1.5 rounded-full hover:bg-gold/10 transition-colors">
                  <Plus size={12} /> ADICIONAR ITEM
                </button>
              </div>
              <div className="space-y-2" data-testid="unit-stock-rows">
                {form.stock.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px_auto] gap-2 items-center">
                    <input required minLength={1} value={s.name} onChange={(e) => setRow("stock", i, "name", e.target.value)} placeholder="Item (ex: Kimono infantil)" className={inputCls} data-testid={`unit-stock-name-${i}`} />
                    <input type="number" min={0} value={s.quantity} onChange={(e) => setRow("stock", i, "quantity", Number(e.target.value))} className={inputCls} data-testid={`unit-stock-qty-${i}`} />
                    <button type="button" onClick={() => removeRow("stock", i)} data-testid={`unit-stock-remove-${i}`} className="w-9 h-9 rounded-lg border border-white/15 flex items-center justify-center text-zinc-500 hover:text-crimson hover:border-crimson/60 transition-colors" aria-label="Remover item">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" disabled={saving} data-testid="unit-submit-button" className="w-full inline-flex items-center justify-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-all duration-300">
              <Building2 size={18} /> {saving ? "Salvando..." : editingId ? "Salvar Alterações" : "Cadastrar Unidade"}
            </button>
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); setFormOpen(false); }} data-testid="unit-edit-cancel" className="w-full font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-8 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
              {editingId ? "CANCELAR EDIÇÃO" : "FECHAR FORMULÁRIO"}
            </button>
          </form>
        )}

        <div className="mt-8" data-testid="units-list-section">
          {units === null ? (
            <p className="text-zinc-500 text-sm">Carregando unidades...</p>
          ) : units.length === 0 ? (
            <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500" data-testid="units-empty">
              <Building2 size={32} />
              <p className="text-sm">Nenhuma unidade cadastrada ainda.</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="units-list">
              {units.map((u, i) => (
                <motion.article
                  key={u.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="bg-[#1C1C23] border border-white/10 hover:border-gold/40 rounded-2xl p-6 sm:p-8 transition-colors"
                  data-testid={`unit-card-${i}`}
                >
                  {u.image && (
                    <img src={u.image} alt={u.name} className="w-full h-44 object-cover rounded-xl border border-white/10 mb-5" data-testid={`unit-image-${i}`} />
                  )}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="font-display font-semibold text-xl">{u.name}</h2>
                      <p className="flex items-center gap-2 text-sm text-zinc-400 mt-1.5" data-testid={`unit-address-${i}`}>
                        <MapPin size={15} className="text-crimson shrink-0" /> {u.address}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(u)} data-testid={`unit-edit-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-gold/50 transition-colors" aria-label={`Editar ${u.name}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => remove(u)} data-testid={`unit-delete-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-crimson hover:border-crimson/60 transition-colors" aria-label={`Excluir ${u.name}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 grid sm:grid-cols-3 gap-6">
                    <div>
                      <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2.5 flex items-center gap-1.5"><Users size={12} className="text-gold" /> INSTRUTORES</p>
                      {(u.instructors || []).length === 0 ? (
                        <p className="text-xs text-zinc-600">Nenhum vinculado.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5" data-testid={`unit-instructors-${i}`}>
                          {u.instructors.map((ins) => (
                            <span key={ins.email} className="font-mono text-[11px] tracking-wider px-2.5 py-1 rounded-full border border-gold/30 text-gold/90">{ins.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2.5 flex items-center gap-1.5"><Clock size={12} className="text-gold" /> TREINOS</p>
                      {(u.schedule || []).length === 0 ? (
                        <p className="text-xs text-zinc-600">Sem horários cadastrados.</p>
                      ) : (
                        <ul className="space-y-1.5" data-testid={`unit-schedule-${i}`}>
                          {(() => {
                            const groups = {};
                            u.schedule.forEach((s) => { (groups[s.day] = groups[s.day] || []).push(s); });
                            return DAYS.filter((d) => groups[d]).map((d) => (
                              <li key={d} className="text-xs text-zinc-300">
                                <span className="font-semibold">{d}</span>
                                <span className="text-zinc-600"> — </span>
                                <span className="font-mono text-gold">
                                  {groups[d].map((s) => `${s.label ? s.label + ": " : ""}${s.start}–${s.end}`).join(" · ")}
                                </span>
                              </li>
                            ));
                          })()}
                        </ul>
                      )}
                    </div>
                    <div>
                      <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2.5 flex items-center gap-1.5"><Package size={12} className="text-gold" /> ESTOQUE</p>
                      {(u.stock || []).length === 0 ? (
                        <p className="text-xs text-zinc-600">Estoque vazio.</p>
                      ) : (
                        <ul className="space-y-1.5" data-testid={`unit-stock-${i}`}>
                          {u.stock.map((s, j) => (
                            <li key={j} className="text-xs text-zinc-300 flex justify-between gap-3">
                              <span>{s.name}</span>
                              <span className={`font-mono ${s.quantity === 0 ? "text-crimson" : "text-gold"}`}>{s.quantity} un.</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </AppShell>
  );
}
